<script lang="ts">
    import dayjs from 'dayjs'
    import DocumentHeader from '../shared/document-header.svelte'
    import type { FormV2Form } from '../../../modules/toolsApi/forms-v2/form-v2-forms/form-v2-form'
    import type { FormV2FormSignature } from '../../../modules/toolsApi/forms-v2/form-v2-form-signatures/form-v2-form-signature'
    import type { FormV2FormSignatureLog } from '../../../modules/toolsApi/forms-v2/form-v2-signature-logs/form-v2-form-signature-log'

    /**
     * Versão estática (sem interatividade) do mesmo conteúdo exibido no popover "Ver histórico de
     * assinaturas" (form-v2-sign-log-popover.svelte) — usada só pela página pública de impressão do
     * relatório, consumida pela lambda-pdf. Mantém os mesmos campos/rótulos daquele popover para as
     * duas visões não divergirem.
     *
     * Exceção proposital: a seção "Declaração de aceite" existe só aqui. O popover é uma consulta
     * interna da equipe, enquanto este relatório é a peça de evidência que acompanha o documento
     * assinado — é nele que o texto aceito por cada signatário precisa constar.
     */
    export let form: FormV2Form
    export let signers: FormV2FormSignature[] = []
    export let logs: FormV2FormSignatureLog[] = []

    const MODE_LINK = 1
    const MODE_EMAIL = 2

    function signatureModeLabel(mode: number | null | undefined) {
        if (mode === MODE_LINK) return 'Link de assinatura (assinatura simples)'
        if (mode === MODE_EMAIL) return 'Assinatura por e-mail (assinatura avançada)'
        return '—'
    }

    function signatureMethodLabel(method: string | null | undefined) {
        if (method === 'a1') return 'Certificado A1 (e-CNPJ ICP-Brasil)'
        if (method === 'self-signed') return 'Avançada (chave da plataforma)'
        return method || '—'
    }

    function formatGeolocation(geo: { latitude: number; longitude: number; accuracy: number } | null | undefined) {
        if (!geo) return 'Não informada (negada ou indisponível)'
        return geo.latitude + ', ' + geo.longitude + ' (precisão: ' + Math.round(geo.accuracy) + 'm)'
    }

    function buildMapsUrl(geo: { latitude: number; longitude: number }) {
        return 'https://www.google.com/maps/search/?api=1&query=' + geo.latitude + ',' + geo.longitude
    }

    function signerFor(log: FormV2FormSignatureLog) {
        return signers.find((s) => s.formV2FormSignatureId === log.formV2FormSignatureId)
    }

    // Só entram se tiverem valor — e nessa ordem, "Assinado em" sempre por último (mesmas regras do
    // popover "Ver histórico de assinaturas").
    function extraRows(log: FormV2FormSignatureLog, signer: FormV2FormSignature | undefined): [string, string][] {
        const rows: [string, string][] = []
        if (signer?.emailSentAt) rows.push(['E-mail enviado em', dayjs(signer.emailSentAt).format('DD/MM/YYYY HH:mm:ss')])
        if (signer?.viewedAt) rows.push(['Visualizado em', dayjs(signer.viewedAt).format('DD/MM/YYYY HH:mm:ss')])
        if (log.createdAt) rows.push(['Assinado em', dayjs(log.createdAt).format('DD/MM/YYYY HH:mm:ss')])
        return rows
    }

    // Textos de aceite distintos entre os assinantes — normalmente um só, mas se o texto tiver
    // mudado entre uma assinatura e outra, o relatório precisa mostrar as duas versões.
    $: consentTexts = [...new Set(logs.map((log) => log.metadata?.consentText).filter(Boolean))] as string[]

    $: documentRows = [
        ['Título', form.title || '—'],
        ['ID do documento', form.formV2FormId],
        ['Modalidade de assinatura', signatureModeLabel(form.signatureMode)],
        ['Quantidade de assinantes', String(signers.length)],
        ['Quantidade de páginas', form.pageCount != null ? String(form.pageCount) : '—'],
        ['Assinado em', form.signedAt ? dayjs(form.signedAt).format('DD/MM/YYYY HH:mm:ss') : '—'],
    ] as [string, string][]
</script>

<div class="fv2-report">
    <DocumentHeader header={form.document?.templateSnapshot?.headerFooter?.header} companyId={form.companyId} />

    <div class="fv2-report-title">Relatório de Assinaturas</div>

    <div class="fv2-report-section">
        <div class="fv2-report-section-title">Documento</div>
        <div class="fv2-report-table">
            {#each documentRows as [field, value] (field)}
                <div class="fv2-report-row">
                    <div class="fv2-report-field">{field}</div>
                    <div class="fv2-report-value">{value}</div>
                </div>
            {/each}
        </div>
    </div>

    {#each logs as log (log.formV2FormSignatureLogId)}
        {@const signer = signerFor(log)}
        {@const geo = log.metadata?.geolocation}
        <div class="fv2-report-section">
            <div class="fv2-report-section-title">{log.personName}</div>
            <div class="fv2-report-table">
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Nome</div>
                    <div class="fv2-report-value">{log.personName}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Documento</div>
                    <div class="fv2-report-value">{log.personDocument}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Telefone</div>
                    <div class="fv2-report-value">{log.personPhone || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">E-mail</div>
                    <div class="fv2-report-value">{log.personEmail || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Endereço</div>
                    <div class="fv2-report-value">{log.personAddress || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">ID único</div>
                    <div class="fv2-report-value">{signer?.friendlySignatureKey || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Método de assinatura</div>
                    <div class="fv2-report-value">{signatureMethodLabel(log.signatureMethod)}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">IP</div>
                    <div class="fv2-report-value">{log.metadata?.requestIP || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">User-Agent</div>
                    <div class="fv2-report-value">{log.metadata?.userAgent || '—'}</div>
                </div>
                <div class="fv2-report-row">
                    <div class="fv2-report-field">Geolocalização</div>
                    <div class="fv2-report-value">
                        {formatGeolocation(geo)}
                        {#if geo}
                            <a class="fv2-report-link" href={buildMapsUrl(geo)} target="_blank" rel="noopener">(ver no mapa)</a>
                        {/if}
                    </div>
                </div>
                {#each extraRows(log, signer) as [field, value] (field)}
                    <div class="fv2-report-row">
                        <div class="fv2-report-field">{field}</div>
                        <div class="fv2-report-value">{value}</div>
                    </div>
                {/each}
            </div>
        </div>
    {/each}

    <!-- Declaração de aceite: sai do texto gravado em cada log (metadata.consentText), não de uma
         constante desta tela — assim o relatório mostra o que foi de fato apresentado na época.
         Assinaturas anteriores a esse registro não trazem `consentText` e ficam de fora, em vez de
         receberem uma declaração que não corresponde ao que aconteceu. -->
    {#if consentTexts.length > 0}
        <div class="fv2-report-section">
            <div class="fv2-report-section-title">Declaração de aceite</div>
            <p class="fv2-report-consent-intro">
                Antes de concluir a assinatura, foi apresentado a cada signatário o texto abaixo, cuja concordância era condição para efetivá-la:
            </p>
            {#each consentTexts as text (text)}
                <p class="fv2-report-consent-text">“{text}”</p>
            {/each}
        </div>
    {/if}

    {#if logs.length === 0}
        <div class="fv2-report-empty">Nenhum registro de assinatura.</div>
    {/if}
</div>

<style>
    .fv2-report {
        max-width: 900px;
        margin: 0 auto;
        background: #fff;
        color: #212529;
    }
    .fv2-report-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 18px 0 14px;
    }
    .fv2-report-section {
        break-inside: avoid;
        margin-bottom: 18px;
    }
    .fv2-report-section-title {
        font-size: 0.95rem;
        font-weight: 600;
        color: #212529;
        border-bottom: 2px solid #dee2e6;
        padding-bottom: 4px;
        margin-bottom: 8px;
    }
    .fv2-report-table {
        display: flex;
        flex-direction: column;
    }
    .fv2-report-row {
        display: grid;
        grid-template-columns: 220px 1fr;
        gap: 10px;
        padding: 5px 0;
        border-bottom: 1px solid #f1f3f5;
        font-size: 0.85rem;
    }
    .fv2-report-field {
        font-weight: 600;
        color: #495057;
    }
    .fv2-report-value {
        color: #212529;
        word-break: break-word;
    }
    .fv2-report-link {
        color: #1c7ed6;
        margin-left: 4px;
    }
    .fv2-report-empty {
        color: #868e96;
        font-size: 0.9rem;
    }
    .fv2-report-consent-intro {
        margin: 0 0 8px;
        font-size: 0.85rem;
        color: #495057;
    }
    .fv2-report-consent-text {
        margin: 0 0 8px;
        font-size: 0.85rem;
        line-height: 1.5;
        color: #212529;
        font-style: italic;
        text-align: justify;
        padding-left: 12px;
        border-left: 3px solid #dee2e6;
    }
</style>
